export type TaskTreeResolution<T> =
  | { status: 'found'; node: T; indexPath: number[]; taskNumberPath: string[] }
  | { status: 'not_found' }
  | { status: 'ambiguous'; taskNumberPaths: string[][] };

/**
 * Resolves a task reference against a recursive task tree (ExamTask.subTasks or
 * SubmissionTask.subTasksSubmission).
 *
 * - An `_id` matches at any depth (unique, no ancestor path needed).
 * - A DOTTED ref ("1.1", "1.1.a") is a root-anchored ancestor path: each dot-segment is consumed one
 *   per tree level. A tree can't have two nodes at the same path, so this can only yield `found` or
 *   `not_found`.
 * - An UNDOTTED ref ("3", "a") matches by a node's own bare `taskNumber`, at any depth. A bare label
 *   can be reused under different parents (e.g. "a" as a subtask of both task 1.1 and task 2), so this
 *   reports `ambiguous` rather than silently picking whichever match a tree walk hits first.
 */
export function resolveTaskTreePath<
  T extends { _id?: unknown; taskNumber?: unknown; subTasks?: unknown; subTasksSubmission?: unknown },
>(nodes: T[] | undefined, ref: string, childKey: 'subTasks' | 'subTasksSubmission'): TaskTreeResolution<T> {
  const target = String(ref ?? '').trim();
  if (!target) return { status: 'not_found' };
  const isDotted = target.includes('.');

  const matches: { node: T; indexPath: number[]; taskNumberPath: string[] }[] = [];

  const walk = (list: T[] | undefined, indexPath: number[], taskNumberPath: string[]): void => {
    if (!Array.isArray(list)) return;
    for (let i = 0; i < list.length; i++) {
      const node = list[i];
      if (!node) continue;
      const nodeIndexPath = [...indexPath, i];
      const taskId = node._id !== undefined ? String(node._id) : '';
      const ownTaskNumber = node.taskNumber !== undefined ? String(node.taskNumber).trim() : '';
      const nodeTaskNumberPath = [...taskNumberPath, ownTaskNumber];

      if (taskId && taskId === target) {
        matches.push({ node, indexPath: nodeIndexPath, taskNumberPath: nodeTaskNumberPath });
        return;
      }
      if (isDotted ? nodeTaskNumberPath.join('.') === target : ownTaskNumber === target) {
        matches.push({ node, indexPath: nodeIndexPath, taskNumberPath: nodeTaskNumberPath });
      }

      walk(node[childKey] as T[] | undefined, nodeIndexPath, nodeTaskNumberPath);
    }
  };

  walk(nodes, [], []);

  if (matches.length === 0) return { status: 'not_found' };
  if (matches.length === 1) {
    const match = matches[0]!;
    return { status: 'found', node: match.node, indexPath: match.indexPath, taskNumberPath: match.taskNumberPath };
  }
  return { status: 'ambiguous', taskNumberPaths: matches.map(m => m.taskNumberPath) };
}

/** Builds the Mongo dot-notation index path (e.g. "tasks.0.subTasks.1") from a resolved `indexPath`. */
export function taskTreeIndexPathString(prefix: string, childKey: string, indexPath: number[]): string {
  let path = `${prefix}.${indexPath[0]}`;
  for (let i = 1; i < indexPath.length; i++) path += `.${childKey}.${indexPath[i]}`;
  return path;
}
